import { createIntl, createIntlCache } from "react-intl";

import {
  formatCurrencyAmountInput,
  getCurrencyFractionDigits,
  isZeroDecimalCurrency,
} from "@/routes/adManager/CurrencyDefaults";
import { messages } from "@/routes/adManager/localization/BidValidation.messages";

import type { IntlShape } from "react-intl";
import type { ISO4217CurrencyCode } from "@/routes/adManager/AdsManagerUtils";
import type { ZeroDecimalCurrencyCodes } from "@/routes/adManager/CurrencyDefaults";
import type { CreationWizardCampaignObjective } from "./creationWizardReducer";

const MICROS_PER_CURRENCY_UNIT = 1_000_000;
const IMPRESSIONS_PER_CPM = 1_000;
const CLICK_MAX_BID_MICROS_MIN = 10_000;
const CLICK_MAX_BID_MICROS_MAX = 100 * MICROS_PER_CURRENCY_UNIT;
const CLICK_MAX_BID_MICROS_MAX_JPY = 16_000 * MICROS_PER_CURRENCY_UNIT;
const CLICK_MAX_BID_MICROS_MAX_KRW = 152_000 * MICROS_PER_CURRENCY_UNIT;
const CONVERSION_MAX_BID_MULTIPLIER = 200;
const CPM_BID_MICROS_MIN = 10_000;
const CPM_BID_MICROS_MAX = 100 * MICROS_PER_CURRENCY_UNIT * IMPRESSIONS_PER_CPM;
const IMPRESSION_MAX_BID_MICROS_MAX = CPM_BID_MICROS_MAX / IMPRESSIONS_PER_CPM;
const DEFAULT_INTL = createIntl({ locale: "en" }, createIntlCache());
export type ClickBilledCampaignObjective = "clicks" | "conversions";
export type MaxBidCapMicrosByCurrency = Record<string, number | undefined>;
export type AdGroupMaxBidCapMicrosByCurrency = {
  clicks: MaxBidCapMicrosByCurrency;
  conversions: MaxBidCapMicrosByCurrency;
  impressions: MaxBidCapMicrosByCurrency;
};

export const CLICK_MAX_BID_MICROS_MAX_BY_CURRENCY: Record<string, number | undefined> = {
  USD: CLICK_MAX_BID_MICROS_MAX,
  AUD: CLICK_MAX_BID_MICROS_MAX,
  NZD: CLICK_MAX_BID_MICROS_MAX,
  CAD: CLICK_MAX_BID_MICROS_MAX,
  GBP: CLICK_MAX_BID_MICROS_MAX,
  KRW: CLICK_MAX_BID_MICROS_MAX_KRW,
  JPY: CLICK_MAX_BID_MICROS_MAX_JPY,
  BRL: CLICK_MAX_BID_MICROS_MAX,
  MXN: CLICK_MAX_BID_MICROS_MAX,
};

export const CONVERSION_MAX_BID_MICROS_MAX_BY_CURRENCY: Record<string, number | undefined> = {
  USD: CLICK_MAX_BID_MICROS_MAX * CONVERSION_MAX_BID_MULTIPLIER,
  AUD: CLICK_MAX_BID_MICROS_MAX * CONVERSION_MAX_BID_MULTIPLIER,
  NZD: CLICK_MAX_BID_MICROS_MAX * CONVERSION_MAX_BID_MULTIPLIER,
  CAD: CLICK_MAX_BID_MICROS_MAX * CONVERSION_MAX_BID_MULTIPLIER,
  GBP: CLICK_MAX_BID_MICROS_MAX * CONVERSION_MAX_BID_MULTIPLIER,
  KRW: CLICK_MAX_BID_MICROS_MAX_KRW * CONVERSION_MAX_BID_MULTIPLIER,
  JPY: CLICK_MAX_BID_MICROS_MAX_JPY * CONVERSION_MAX_BID_MULTIPLIER,
  BRL: CLICK_MAX_BID_MICROS_MAX * CONVERSION_MAX_BID_MULTIPLIER,
  MXN: CLICK_MAX_BID_MICROS_MAX * CONVERSION_MAX_BID_MULTIPLIER,
};

export const IMPRESSION_MAX_BID_MICROS_MAX_BY_CURRENCY: Record<string, number | undefined> = {
  USD: IMPRESSION_MAX_BID_MICROS_MAX,
  AUD: IMPRESSION_MAX_BID_MICROS_MAX,
  NZD: IMPRESSION_MAX_BID_MICROS_MAX,
  CAD: IMPRESSION_MAX_BID_MICROS_MAX,
  GBP: IMPRESSION_MAX_BID_MICROS_MAX,
  KRW: IMPRESSION_MAX_BID_MICROS_MAX,
  JPY: IMPRESSION_MAX_BID_MICROS_MAX,
  BRL: IMPRESSION_MAX_BID_MICROS_MAX,
  MXN: IMPRESSION_MAX_BID_MICROS_MAX,
};

export const FALLBACK_AD_GROUP_MAX_BID_CAP_MICROS_BY_CURRENCY: AdGroupMaxBidCapMicrosByCurrency = {
  clicks: CLICK_MAX_BID_MICROS_MAX_BY_CURRENCY,
  conversions: CONVERSION_MAX_BID_MICROS_MAX_BY_CURRENCY,
  impressions: IMPRESSION_MAX_BID_MICROS_MAX_BY_CURRENCY,
};

export const FALLBACK_CLICK_MAX_BID_AMOUNT_BY_CURRENCY: Record<string, string | undefined> = {
  USD: "3.50",
  AUD: "5.00",
  NZD: "6.00",
  CAD: "5.00",
  GBP: "2.60",
  KRW: "5300.00",
  JPY: "560.00",
  BRL: "20.00",
  MXN: "70.00",
};

export function isClickCampaignObjective(
  objective: CreationWizardCampaignObjective,
): objective is "clicks" | "conversions" {
  return objective === "clicks" || objective === "conversions";
}

export function getDefaultClickMaxBidAmountForObjective(
  objective: CreationWizardCampaignObjective,
  defaultClickMaxBidAmount = FALLBACK_CLICK_MAX_BID_AMOUNT_BY_CURRENCY.USD ?? "",
): string {
  return isClickCampaignObjective(objective) ? defaultClickMaxBidAmount : "";
}

export function getDefaultClickMaxBidAmountForCurrency(
  currency: string | null | undefined,
  defaultClickMaxBidAmountsByCurrency: Record<
    string,
    string | undefined
  > = FALLBACK_CLICK_MAX_BID_AMOUNT_BY_CURRENCY,
): string {
  const normalizedCurrency = currency?.trim().toUpperCase();
  if (!normalizedCurrency) {
    return defaultClickMaxBidAmountsByCurrency.USD ?? "";
  }
  return defaultClickMaxBidAmountsByCurrency[normalizedCurrency] ?? "";
}

export function getClickObjectiveMaxBidValidationError(
  maxBidAmount: string | undefined,
  currency: ISO4217CurrencyCode,
  intl: IntlShape = DEFAULT_INTL,
  objective: ClickBilledCampaignObjective = "clicks",
  maxBidCapMicrosByCurrency: AdGroupMaxBidCapMicrosByCurrency = FALLBACK_AD_GROUP_MAX_BID_CAP_MICROS_BY_CURRENCY,
  zeroDecimalCurrencyCodes?: ZeroDecimalCurrencyCodes,
): string | null {
  const minBidMicros = CLICK_MAX_BID_MICROS_MIN;
  const maxBidMicros =
    objective === "conversions" ?
      maxBidCapMicrosByCurrency.conversions[currency]
    : maxBidCapMicrosByCurrency.clicks[currency];
  if (!minBidMicros || !maxBidMicros) {
    return intl.formatMessage(messages.unsupportedCurrencyError);
  }
  const minBidAmount =
    isZeroDecimalCurrency(currency, zeroDecimalCurrencyCodes) ?
      Math.ceil(minBidMicros / MICROS_PER_CURRENCY_UNIT)
    : minBidMicros / MICROS_PER_CURRENCY_UNIT;
  const maxBidAmountLimit = maxBidMicros / MICROS_PER_CURRENCY_UNIT;
  const minimumAmount = formatCurrencyAmountInput({
    amount: minBidAmount,
    currency,
    zeroDecimalCurrencyCodes,
  });
  const maximumAmount = formatCurrencyAmountInput({
    amount: maxBidAmountLimit,
    currency,
    zeroDecimalCurrencyCodes,
  });

  const trimmedMaxBidAmount = maxBidAmount?.trim() ?? "";
  if (!trimmedMaxBidAmount) {
    return intl.formatMessage(messages.maxBidRangeError, {
      minimumAmount,
      maximumAmount,
    });
  }

  const parsedMaxBidAmount = Number(trimmedMaxBidAmount);
  if (!Number.isFinite(parsedMaxBidAmount)) {
    return intl.formatMessage(messages.numericMaxBidError);
  }
  if (
    isZeroDecimalCurrency(currency, zeroDecimalCurrencyCodes) &&
    !Number.isInteger(parsedMaxBidAmount)
  ) {
    return intl.formatMessage(messages.wholeMaxBidError, { currency });
  }

  if (parsedMaxBidAmount < minBidAmount || parsedMaxBidAmount > maxBidAmountLimit) {
    return intl.formatMessage(messages.maxBidRangeError, {
      minimumAmount,
      maximumAmount,
    });
  }

  return null;
}

export function getImpressionObjectiveMaxBidValidationError(
  maxBidAmount: string | undefined,
  intl: IntlShape = DEFAULT_INTL,
  currency: ISO4217CurrencyCode = "USD",
  maxBidCapMicrosByCurrency: AdGroupMaxBidCapMicrosByCurrency = FALLBACK_AD_GROUP_MAX_BID_CAP_MICROS_BY_CURRENCY,
  zeroDecimalCurrencyCodes?: ZeroDecimalCurrencyCodes,
): string | null {
  const maxBidMicrosMax = maxBidCapMicrosByCurrency.impressions[currency];
  if (!maxBidMicrosMax) {
    return intl.formatMessage(messages.unsupportedCurrencyError);
  }
  const cpmBidMin =
    isZeroDecimalCurrency(currency, zeroDecimalCurrencyCodes) ?
      Math.ceil(CPM_BID_MICROS_MIN / MICROS_PER_CURRENCY_UNIT)
    : CPM_BID_MICROS_MIN / MICROS_PER_CURRENCY_UNIT;
  const cpmBidMax = perImpressionMicrosToCpmBidMicros(maxBidMicrosMax) / MICROS_PER_CURRENCY_UNIT;
  const minimumAmount = formatCurrencyAmountInput({
    amount: cpmBidMin,
    currency,
    zeroDecimalCurrencyCodes,
  });
  const maximumAmount = formatCurrencyAmountInput({
    amount: cpmBidMax,
    currency,
    zeroDecimalCurrencyCodes,
  });

  const trimmedMaxBidAmount = maxBidAmount?.trim() ?? "";
  if (!trimmedMaxBidAmount) {
    return intl.formatMessage(messages.cpmBidRangeError, {
      minimumAmount,
      maximumAmount,
    });
  }

  const parsedMaxBidAmount = Number(trimmedMaxBidAmount);
  if (!Number.isFinite(parsedMaxBidAmount)) {
    return intl.formatMessage(messages.numericCpmBidError);
  }
  if (
    currency != null &&
    getCurrencyFractionDigits(currency, zeroDecimalCurrencyCodes) === 0 &&
    !Number.isInteger(parsedMaxBidAmount)
  ) {
    return intl.formatMessage(messages.wholeMaxBidError, { currency });
  }

  if (parsedMaxBidAmount < cpmBidMin || parsedMaxBidAmount > cpmBidMax) {
    return intl.formatMessage(messages.cpmBidRangeError, {
      minimumAmount,
      maximumAmount,
    });
  }

  return null;
}

export function cpmBidMicrosToPerImpressionMicros(cpmBidMicros: number): number {
  return Math.round(cpmBidMicros / IMPRESSIONS_PER_CPM);
}

export function perImpressionMicrosToCpmBidMicros(perImpressionMicros: number): number {
  return perImpressionMicros * IMPRESSIONS_PER_CPM;
}
